import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useCallback } from 'react';

import type {
  AddCardInput,
  DropCardInput,
} from '@sorare/core/src/__generated__/globalTypes';
import { commonCardMarketplaceFragment } from '@sorare/core/src/contexts/currentUser/queries';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { useGetInteractionContext } from '@sorare/core/src/hooks/useGetInteractionContext';

import type {
  DropAndAddCards,
  DropAndAddCardsVariables,
} from './__generated__/useDropAndAddCards.graphql';

const DROP_AND_ADD_CARDS_MUTATION = gql`
  mutation DropAndAddCards($input: dropAndAddCardsInput!, $sport: Sport!) {
    dropAndAddCards(input: $input) {
      currentUser {
        slug
        ...commonCardMarketplace_currentUser
      }
      addedCards {
        slug
      }
      droppedCards {
        slug
        user {
          slug
        }
      }
      errors {
        code
        message
      }
    }
  }
  ${commonCardMarketplaceFragment}
` as TypedDocumentNode<DropAndAddCards, DropAndAddCardsVariables>;

export const useDropAndAddCards = () => {
  const { sport } = useSportContext();
  const [mutate, { loading }] = useMutation(DROP_AND_ADD_CARDS_MUTATION, {
    showErrorsWithSnackNotification: true,
  });
  const getInteractionContext = useGetInteractionContext();

  const dropAndAddCards = useCallback(
    async (
      droppedCards: DropCardInput[],
      addedCards: AddCardInput[],
      {
        attachCardsToUserCollections = false,
      }: { attachCardsToUserCollections?: boolean } = {}
    ) => {
      return mutate({
        variables: {
          input: {
            addedCards,
            droppedCards,
            attachCardsToUserCollections,
            interactionContext: getInteractionContext(),
          },
          sport,
        },
      });
    },
    [mutate, sport, getInteractionContext]
  );
  return { dropAndAddCards, loading };
};
